import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";

import { api_without_cancellation } from "../../../api/api";
import { getCaseId, getClientId, inputCurrencyFormat2 } from "../../../Utils/helper";
import SelectStateModal from "../../TreatmentPage/modals/state-modal/SelectStateModal";

const TabGrid = ({ tabs, selectedId, onSelect }) => (
  <div className="w-100" style={{ minHeight: "25px" }}>
    <div
      className="overflow-scroll invisible-scrollbar"
      style={{
        display: "grid",
        gridTemplateRows: `repeat(${Math.ceil((tabs?.length ?? 0) / 4)}, 1fr)`,
        gridAutoFlow: "column",
        gap: "5px",
      }}
    >
      {tabs?.map((item) => {
        const isSelected = selectedId === item.id;
        return (
          <div key={item.id} style={{ position: "relative", display: "flex" }}>
            <button
              type="button"
              className={`event-type-button btn-hover-event-litigation-act height-25 ${
                isSelected ? "selected" : "unselected"
              }`}
              onClick={() => {
                if (!item.disabled) onSelect(item.id);
              }}
              disabled={item.disabled}
              style={{
                flex: 1,
                cursor: item.disabled ? "not-allowed" : "pointer",
                opacity: item.disabled ? 0.5 : 1,
              }}
            >
              <span className="text-nowrap">{item.label}</span>
            </button>
          </div>
        );
      })}
    </div>
  </div>
);

const hasValidAmount = (value) => {
  const amount = parseFloat(value);
  return Number.isFinite(amount) && amount > 0;
};

const normalizeAmount = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  return String(value);
};

const getCardValue = (card) =>
  card?.id ?? card?.card_id ?? card?.last_four ?? card?.last4 ?? card?.card_number ?? "";

const getCardLabel = (card) => {
  const name = card?.name || card?.card_name || card?.nickname || card?.bank_name || "Credit Card";
  const lastFour = card?.last_four || card?.last4 || card?.last_4 || card?.card_number;

  return lastFour ? `${name} ***${String(lastFour).slice(-4)}` : name;
};

const CostRequestDetailedModal = ({
  show,
  onClose,
  page_id,
  source_model,
  source_id,
  requests = [],
}) => {
  const availableRequests = useMemo(
    () => requests.filter((request) => !request.disabled),
    [requests]
  );
  const [activeRequestId, setActiveRequestId] = useState("");
  const activeRequest = useMemo(
    () =>
      requests.find((request) => request.id === activeRequestId) ??
      availableRequests[0] ??
      requests[0] ??
      {},
    [activeRequestId, availableRequests, requests]
  );

  const defaultValues = useMemo(
    () => ({
      amount: normalizeAmount(activeRequest.amount),
      payee: activeRequest.name ?? "",
      address1: activeRequest.address1 ?? "",
      address2: activeRequest.address2 ?? "",
      city: activeRequest.city ?? "",
      state: activeRequest.state ?? "",
      zip: activeRequest.zip_code ?? "",
      memo: "",
      invoice_number: "",
      note: "",
      paid_by: "Credit Card",
      credit_card: "",
      check_number: "",
      date: "",
    }),
    [
      activeRequest.address1,
      activeRequest.address2,
      activeRequest.amount,
      activeRequest.city,
      activeRequest.name,
      activeRequest.state,
      activeRequest.zip_code,
    ]
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors: formErrors },
  } = useForm({ defaultValues });

  const [submitError, setSubmitError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [firmCards, setFirmCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [statesAbrs, setStatesAbrs] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  register("state");

  const payeeState = watch("state");
  const paidBy = watch("paid_by");

  const handleStateShow = () => setShowStateDropdown((s) => !s);
  const handleStateClose = () => setShowStateDropdown(false);

  useEffect(() => {
    if (!show) return;

    const firstAvailableRequest = availableRequests[0] ?? requests[0];
    setActiveRequestId(firstAvailableRequest?.id ?? "");
  }, [availableRequests, requests, show]);

  useEffect(() => {
    const fetchStatesData = async () => {
      try {
        const response = await api_without_cancellation.get("/api/states/");
        if (response.status === 200) {
          setStatesAbrs(response.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchPages = async () => {
      try {
        const response = await api_without_cancellation.get("/api/getPages/");
        setPages(response.data.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchStatesData();
    fetchPages();
  }, []);

  useEffect(() => {
    if (!show) return;

    setSubmitError(false);
    reset(defaultValues);

    const fetchFirmCards = async () => {
      setCardsLoading(true);
      try {
        const response = await api_without_cancellation.get("/api/settlement-page/get-firm-cards/");
        const cards = Array.isArray(response.data)
          ? response.data
          : response.data?.data || response.data?.cards || [];
        setFirmCards(cards);
      } catch (error) {
        console.error(error);
        setFirmCards([]);
      } finally {
        setCardsLoading(false);
      }
    };

    fetchFirmCards();
  }, [defaultValues, reset, show]);

  useEffect(() => {
    if (!page_id || pages.length === 0) {
      setSelectedPage(null);
      return;
    }

    const match = pages.find((page) => Number(page.id) === Number(page_id));
    setSelectedPage(match ?? null);
  }, [page_id, pages]);

  const handleCancel = () => {
    reset(defaultValues);
    onClose();
  };

  const onSubmit = async (values) => {
    const parsedAmount = parseFloat(values.amount);
    const payload = {
      source_model,
      source_id,
      source_key: activeRequest.source_key,
      case_id: getCaseId(),
      client_id: getClientId(),
      page_id,
      amount: parsedAmount.toFixed(2),
      cost_name: activeRequest.cost_name ?? "",
      first_name: activeRequest.first_name ?? "",
      middle_name: activeRequest.middle_name ?? "",
      last_name: activeRequest.last_name ?? "",
      name: values.payee,
      address1: values.address1 ?? "",
      address2: values.address2 ?? "",
      city: values.city ?? "",
      state: values.state ?? "",
      zip_code: values.zip ?? "",
      phone_number: activeRequest.phone_number ?? "",
      fax_number: activeRequest.fax_number ?? "",
      ext: activeRequest.ext ?? "",
      email: activeRequest.email ?? "",
      date: values.date || null,
      invoice_number: values.invoice_number ?? "",
      memo: values.memo ?? "",
      note: values.note ?? "",
      paid_by: values.paid_by,
      credit_card: values.paid_by === "Credit Card" ? values.credit_card : "",
      check_number: values.paid_by === "Check" ? values.check_number : "",
      cost_type: "Cost",
    };

    setLoading(true);
    setSubmitError(false);
    try {
      await api_without_cancellation.post("/api/cost-requests/", payload);
      reset(defaultValues);
      onClose();
    } catch (error) {
      console.error("Error creating cost request:", error.response ? error.response.data : error);
      setSubmitError(true);
    } finally {
      setLoading(false);
    }
  };
  const [amountFocused, setAmountFocused] = useState(false);
  const [previousAmount, setPreviousAmount] = useState("");

  const amountValue = watch("amount");
  return (
    <>
      <Modal show={show} onHide={onClose} dialogClassName="modal-dialog-centered modal-800p">
        <div
          className="text-center height-25 p-0 bg-primary modal-header"
          style={{ border: "none" }}
        >
          <div
            className="mx-auto height-25 text-white d-flex align-items-center"
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            Cost Request
          </div>
        </div>
        <Modal.Body style={{ padding: "5px 5px 0px 5px" }}>
          {submitError ? <Alert variant="danger">Error creating a cost request.</Alert> : <></>}
          <TabGrid tabs={requests} selectedId={activeRequest.id} onSelect={setActiveRequestId} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="w-100 m-b-5" style={{ minHeight: "355px" }}>
              <div className="d-flex height-25 m-t-5">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }}>
                  Amount:
                </div>
                <div className="col-10 d-flex-1 p-0 d-flex align-items-center position-relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={amountFocused ? "" : "0.00"}
                    className="form-control rounded-0 height-25"
                    style={{ paddingLeft: "18px", borderColor: formErrors.amount ? "red" : "" }}
                    value={
                      amountFocused
                        ? `$ ${amountValue ?? ""}`
                        : amountValue !== "" && amountValue !== null
                          ? inputCurrencyFormat2(amountValue)
                          : ""
                    }
                    {...register("amount", {
                      required: "Required",
                      validate: (value) =>
                        hasValidAmount(value) ? true : "Amount must be positive",
                      onChange: (e) => {
                        let value = e.target.value.replace(/[^0-9.]/g, "");

                        if ((value.match(/\./g) || []).length > 1) {
                          value = value.substring(0, value.lastIndexOf("."));
                        }

                        if (value.includes(".")) {
                          const parts = value.split(".");
                          if (parts[1].length > 4) {
                            parts[1] = parts[1].substring(0, 4);
                          }
                          value = parts.join(".");
                        }

                        setValue("amount", value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      },
                    })}
                    onFocus={() => {
                      setPreviousAmount(amountValue);
                      setValue("amount", "", { shouldDirty: true });
                      setAmountFocused(true);
                    }}
                    onBlur={() => {
                      setAmountFocused(false);

                      if (!amountValue) {
                        setValue("amount", previousAmount, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                  />
                </div>
              </div>

              <div className="d-flex height-25 m-t-5">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }}>
                  Payee:
                </div>
                <div className="col-10 d-flex-1 p-0">
                  <input
                    type="text"
                    className="form-control rounded-0 height-25"
                    placeholder="Payee"
                    {...register("payee", {
                      required: "Required",
                      validate: (value) => value.trim().length > 0 || "Payee must not be empty",
                    })}
                  />
                  {formErrors.payee && (
                    <div className="text-danger">{formErrors.payee.message}</div>
                  )}
                </div>
              </div>

              <div className="row mx-0 align-items-center custom-margin-bottom m-t-5">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }}>
                  Payee Address:
                </div>
                <div className="d-flex col-10 p-0">
                  <div className="d-flex-1 p-r-5">
                    <input
                      type="text"
                      placeholder="Address 1"
                      className="form-control rounded-0 height-25"
                      {...register("address1")}
                    />
                  </div>
                  <div className="d-flex-1">
                    <input
                      type="text"
                      placeholder="Address 2"
                      className="form-control rounded-0 height-25"
                      {...register("address2")}
                    />
                  </div>
                </div>
              </div>

              <div className="row mx-0 align-items-center custom-margin-bottom">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }} />
                <div className="d-flex col-10 p-0">
                  <div className="d-flex-1 p-l-0 p-r-5">
                    <input
                      type="text"
                      placeholder="City"
                      className="form-control rounded-0 height-25"
                      {...register("city")}
                    />
                  </div>
                  <div
                    className="d-flex-1 p-l-0 position-relative height-25 p-r-5 custom-select-new-provider"
                    style={{ maxWidth: "90px" }}
                  >
                    <div
                      className="dropdown-button rounded-0 p-0 p-l-5 form-control height-25 d-flex align-items-center"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleStateShow();
                        }
                      }}
                      onClick={handleStateShow}
                    >
                      <span id="selectedOption">
                        {payeeState ? (
                          <div className="d-flex align-items-center">
                            <svg
                              style={{
                                width: "15px",
                                height: "15px",
                                fill: "var(--primary-80)",
                                color: "var(--primary-80)",
                                stroke: "var(--primary-80)",
                              }}
                              className={`icon icon-state-${payeeState}`}
                            >
                              <use xlinkHref={`#icon-state-${payeeState}`} />
                            </svg>
                            {payeeState}
                          </div>
                        ) : (
                          "State"
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="d-flex-1 pe-0" style={{ maxWidth: "90px" }}>
                    <input
                      type="text"
                      placeholder="Zip"
                      className="form-control rounded-0 height-25"
                      {...register("zip")}
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex height-25 m-t-5">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }}>
                  Check Memo:
                </div>
                <div className="col-10 d-flex-1 p-0">
                  <input
                    type="text"
                    placeholder="Memo"
                    className="form-control rounded-0 height-25"
                    {...register("memo")}
                  />
                </div>
              </div>

              <div className="d-flex height-25 m-t-5">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }}>
                  Invoice Number:
                </div>
                <div className="col-10 d-flex-1 p-0">
                  <input
                    type="text"
                    className="form-control rounded-0 height-25"
                    placeholder="Invoice Number"
                    {...register("invoice_number")}
                  />
                </div>
              </div>

              <div className="d-flex height-25 m-t-5">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }}>
                  Category:
                </div>
                <div className="col-10 p-l-5 p-r-5">
                  <div className="d-flex align-items-center">
                    <img
                      src={selectedPage?.page_icon}
                      style={{ width: "15px", height: "15px", marginRight: "5px" }}
                      alt=""
                    />
                    {selectedPage?.name}
                    {activeRequest.category_suffix && (
                      <span style={{ marginLeft: "5px" }}> - {activeRequest.category_suffix}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="d-flex m-t-5">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }}>
                  Note:
                </div>
                <div className="col-10 d-flex-1 p-0">
                  <textarea
                    className="form-control rounded-0"
                    style={{ height: "50px", resize: "none" }}
                    {...register("note")}
                  />
                </div>
              </div>

              <div className="d-flex m-t-5">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }}>
                  Paid By:
                </div>

                <div className="col-10 d-flex-1 p-0">
                  <div className="d-flex align-items-center m-b-5 height-25">
                    <label className="d-flex align-items-center mb-0 col-3 p-l-5 p-r-5">
                      <input
                        type="radio"
                        value="Pending"
                        className="m-r-5 cursor-pointer"
                        {...register("paid_by", {
                          onChange: (e) => {
                            if (e.target.value === "Pending") {
                              setValue("check_number", "");
                            }
                          },
                        })}
                      />
                      Payment Pending
                    </label>
                  </div>
                  <div className="d-flex align-items-center m-b-5 height-25 custom-select-new-provider">
                    <label className="d-flex align-items-center mb-0 col-3 p-l-5 p-r-5">
                      <input
                        type="radio"
                        value="Credit Card"
                        className="m-r-5 cursor-pointer"
                        {...register("paid_by", {
                          onChange: (e) => {
                            if (e.target.value === "Credit Card") {
                              setValue("check_number", "");
                            }
                          },
                        })}
                      />
                      Credit Card
                    </label>

                    <select
                      className="form-control rounded-0 height-25 py-0  p-l-5 p-r-5"
                      {...register("credit_card", {
                        onChange: (e) => {
                          if (e.target.value) {
                            setValue("paid_by", "Credit Card");
                            setValue("check_number", "");
                          }
                        },
                      })}
                      onClick={() => {
                        if (paidBy !== "Credit Card") {
                          setValue("paid_by", "Credit Card");
                          setValue("check_number", "");
                        }
                      }}
                    >
                      <option value="">{cardsLoading ? "Loading cards..." : "Select Card"}</option>
                      {firmCards?.map((card) => {
                        const value = getCardValue(card);

                        return (
                          <option key={value || getCardLabel(card)} value={value}>
                            {getCardLabel(card)}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="d-flex align-items-center height-25">
                    <label className="d-flex align-items-center mb-0 col-3 p-l-5 p-r-5">
                      <input
                        type="radio"
                        value="Check"
                        className="m-r-5 cursor-pointer"
                        {...register("paid_by", {
                          onChange: (e) => {
                            if (e.target.value === "Check") {
                              setValue("credit_card", "");
                            }
                          },
                        })}
                      />
                      Check
                    </label>

                    <input
                      type="text"
                      placeholder="Check Number"
                      className="form-control rounded-0 height-25  p-l-5 p-r-5"
                      {...register("check_number", {
                        onChange: (e) => {
                          if (e.target.value) {
                            setValue("paid_by", "Check");
                            setValue("credit_card", "");
                          }
                        },
                      })}
                      onClick={() => {
                        if (paidBy !== "Check") {
                          setValue("paid_by", "Check");
                          setValue("credit_card", "");
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex height-25 m-t-5">
                <div className="col-2 text-end ps-0 p-r-5" style={{ color: "var(--primary-25)" }}>
                  Date Due:
                </div>
                <div className="col-10 d-flex-1 p-0">
                  <input
                    type="date"
                    className="form-control rounded-0 height-25"
                    {...register("date")}
                  />
                </div>
              </div>
            </div>

            <div className="d-flex mt-auto w-100 justify-content-between p-b-5">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                className="height-25"
                style={{ padding: "0px 12px" }}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                type="submit"
                className="m-l-5 bg-success height-25"
                style={{ padding: "0px 12px" }}
                disabled={loading || activeRequest.disabled}
              >
                {loading ? "Request Cost and Close" : "Request Cost and Close"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      {showStateDropdown && (
        <SelectStateModal
          show={showStateDropdown}
          handleClose={handleStateClose}
          onChange={(s) => setValue("state", s?.StateAbr)}
          statesData={statesAbrs}
        />
      )}
    </>
  );
};

export default CostRequestDetailedModal;
